import { ResizeObserver } from '@juggle/resize-observer';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Check } from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { animated, useSpring } from '@react-spring/web';
import { ComponentProps, useCallback } from 'react';
import useMeasure from 'react-use-measure';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { RowDivider } from '../RowDivider';
import { CheckoutProcessStep } from './types/CheckoutProcessStep.enum';

type Props = {
    activeStep: CheckoutProcessStep;
    children: React.ReactNode;
    loading: boolean;
    onEdit?: (event: React.SyntheticEvent) => void;
    onSave: () => void;
    readOnly: boolean;
    saveButtonText: React.ReactNode;
    step: CheckoutProcessStep;
    stepRef?: React.MutableRefObject<HTMLDivElement>;
    title: React.ReactNode;
    valid: boolean;
};

export const CheckoutStep = ({
    activeStep,
    children,
    loading,
    onEdit,
    onSave,
    readOnly,
    saveButtonText,
    step,
    stepRef,
    title,
    valid,
}: Props) => {
    const {
        breakpoints: { tabletWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;
    const formatMessage = useAppFormatMessage();
    const mobileSize = useIsNarrowerThan(tabletWidthNum + 1);
    const active = step === activeStep;
    const beforeActive = step < activeStep;

    const showEditAction = beforeActive && valid && !readOnly;
    const showContent = beforeActive || active;

    const [contentContainerRef, { height }] = useMeasure({
        polyfill: ResizeObserver,
    });
    const slideInStyles = useSpring({
        to: {
            height: height,
            opacity: showContent ? 1 : 0,
        },
    });

    const onClickEditSection: ComponentProps<typeof Link>['onClick'] = useCallback(
        (event) => {
            if (!loading) {
                onEdit(event);
            }
        },
        [loading, onEdit]
    );

    return (
        <section ref={stepRef}>
            <div className="mb-md flex justify-between">
                <H5
                    className={cn({
                        'text-text-secondary': !active && activeStep !== CheckoutProcessStep.Review,
                    })}
                >
                    {title}
                </H5>
                {showEditAction && (
                    <Check
                        aria-hidden="true"
                        className="inline-flex"
                        data-testid={`${title}-valid`}
                        size="sm"
                    />
                )}
            </div>
            <div className="mb-lg flex justify-between">
                <animated.div
                    className="w-full overflow-hidden"
                    style={slideInStyles}
                >
                    {showContent && <div ref={contentContainerRef}>{children}</div>}
                </animated.div>
                {showEditAction && onEdit && (
                    <Link
                        onClick={onClickEditSection}
                        size="sm"
                    >
                        {formatMessage('checkout', 'stepEdit')}
                    </Link>
                )}
            </div>
            <div
                className={cn('mb-lg', {
                    'smMax:mb-sm': active,
                })}
            >
                {active && (
                    <Button
                        className="max-w-fit p-sm smMax:w-full smMax:max-w-full"
                        disabled={!valid}
                        loading={loading}
                        onClick={onSave}
                        size={mobileSize ? 'md' : 'sm'}
                    >
                        {saveButtonText}
                    </Button>
                )}
            </div>
            {beforeActive && <RowDivider />}
            {active && <RowDivider className="mb-2xl smMax:mb-md" />}
        </section>
    );
};
